/* eslint-disable camelcase */
import React from 'react'
import Layout from '@components/Layout'
import { graphql } from 'gatsby'
import InsightsDetailContent from '@sections/InsightsDetailContent'
import { getHrefLangUrls, handleSEO } from '@utils'
import TitleAndMetaTags from '@components/TitleAndMetaTags'
import { useGtag, useLocale } from '@hooks'

const InsightsDetail = ({ data, pageContext }) => {
  const { getLocalizedSlug } = useLocale()

  useGtag({
    action: data?.insight?.category?.title,
    categories: data?.insight?.associatedPracticeAreas?.map(
      ({ title }) => title
    ),
    label: getLocalizedSlug({
      slug: data?.insight?.slug,
      contentType: data?.insight?.__typename,
      category: data?.insight?.category?.slug,
    }),
    skipEventEmission:
      data?.insight?.category?.title !== 'Client Alerts' &&
      data?.insight?.category?.title !== 'Accolades',
  })

  return (
    <Layout {...pageContext} contentType="article">
      <InsightsDetailContent
        {...data}
        next={pageContext?.nextEntries}
        practiceAreas={data?.insight?.associatedPracticeAreas}
        parentPath={data?.parentPage?.slug}
      />
    </Layout>
  )
}

export default InsightsDetail

export const query = graphql`
  query insightsDetailV2(
    $contentful_id: String!
    $node_locale: String!
    $displayOnSitesAsNodeLocales: [String!]
  ) {
    parentPage: contentfulPage(
      contentful_id: { eq: "5if9xKprWvNrUyyfWPk9vc" }
      node_locale: { eq: $node_locale }
    ) {
      ...basePageFields
      parentId: contentful_id
    }
    insight: contentfulArticle(
      contentful_id: { eq: $contentful_id }
      node_locale: { eq: $node_locale }
    ) {
      ...baseArticleFields
      customPdfUpload {
        file {
          url
        }
        title
      }
      useByline
      bylineHtml: childContentfulArticleBylineHtmlJsonNode {
        useHTMLField
        html
      }
      authors {
        ...baseProfessionalFields
      }
      associatedPracticeAreas: practiceAreas {
        ...basePracticeAreaFields
      }
      associatedProfessionals {
        ...baseProfessionalFields
        offices {
          ...baseOfficeFields
        }
        image {
          ...assetBase
        }
      }
      forMoreInformation {
        ...baseProfessionalFields
        image {
          ...assetBase
        }
      }
      sidebarTitle
      contentHtml: childContentfulArticleContentHtmlJsonNode {
        useHTMLField
        html
      }
      content {
        raw
        references {
          ...assetBase
          ...articleLinkFields
          ...articleCategoryLinkFields
          ...eventLinkFields
          ...filterPageLinkFields
          ...newsLinkFields
          ...officeLinkFields
          ...pageLinkFields
          ...practiceAreaLinkFields
          ...professionalLinkFields
          ...moduleAccordion
          ...moduleMediaEmbed
          ...moduleList
          # ...moduleProfessionalsTable
          ...moduleDisclaimer
          # ...moduleProfessionalsBlock
          ...professionalFields
        }
      }
      articleGrid {
        ...moduleArticleGrid
      }
      seo {
        ...seoMetadata
      }
    }
    currentPageWithAllLocales: allContentfulArticle(
      filter: {
        node_locale: { in: $displayOnSitesAsNodeLocales }
        contentful_id: { eq: $contentful_id }
      }
    ) {
      nodes {
        slug
        node_locale
        category {
          slug
        }
      }
    }
  }
`

export const Head = ({
  data: { insight, currentPageWithAllLocales },
  pageContext: { displayLocale, parentPages, localizedSlug, node_locale },
  path,
}) => {
  const { title, content, contentHtml, practiceAreas, seo } = insight

  const { seoTitle, seoDescription, seoKeywords, seoImage } = handleSEO(
    seo,
    {
      title: title,
      content: content,
      contentHtml: contentHtml,
      practiceAreas: practiceAreas,
    },
    displayLocale,
    'article'
  )

  const hrefLangUrls = getHrefLangUrls(
    currentPageWithAllLocales?.nodes,
    parentPages
  )

  return (
    <TitleAndMetaTags
      hrefLangUrls={hrefLangUrls}
      title={seoTitle}
      description={seoDescription}
      keywords={seoKeywords}
      image={seoImage}
      canonicalSlug={localizedSlug}
      locale={node_locale}
      usDefault={seo?.enUsCanonicalUrl}
      noIndex={seo?.noIndex}
      noArchive={seo?.noArchive}
    />
  )
}
